import React, { type FC, useEffect } from 'react'

import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useDeleteApplyV2ProfileMutation } from '@core/app/api/applyJobV2Api'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import './CloseApplyModal.scss'

const baseClass = 'CloseApplyModal'

interface CloseApplyModalProps {
    isModalOpen: boolean
    setIsModalOpen: (isOpen: boolean) => void
    onCloseApply: () => void
}

const CloseApplyModal: FC<CloseApplyModalProps> = ({ isModalOpen, setIsModalOpen, onCloseApply }) => {
    const { t } = useTranslation()
    const { storageData, isOpen } = useApplyV2Context()
    const id = storageData?.id?.toString() || ''
    const location = useLocation()
    const navigate = useNavigate()
    // const [deleteApplyV2Profile] = useDeleteApplyV2ProfileMutation()


    const handleExit = async () => {
        // Remove "&apply_only" from URL if present
        if (location.search.includes('&apply_only') || location.search.includes('?apply_only')) {
            const searchParams = new URLSearchParams(location.search)
            searchParams.delete('apply_only')
            const newSearch = searchParams.toString()
            const newPath = location.pathname + (newSearch ? `?${newSearch}` : '')
            navigate(newPath, { replace: true })
        }

        // const uid = storageData?.postInitUid
        // if (uid) {
        //     try {
        //         await deleteApplyV2Profile({ uid })
        //     } catch (error) {
        //         console.error('Failed to delete apply profile:', error)
        //     }
        //     onCloseApply()
        // }
        onCloseApply()
    }

    return (
        <Modal
            isCentered
            size={'xl'}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            motionPreset="slideInBottom"
        >
            <ModalOverlay className={`${baseClass}__overlay`} />

            <ModalContent className={`${baseClass}__content`}>
                <ModalHeader className={`${baseClass}__header`}>
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
                            fill="#FCEAEA"
                        />
                        <g clipPath="url(#clip0_9205_20709)">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12.916 22.0001C12.916 16.9835 16.9828 12.9167 21.9993 12.9167C27.0159 12.9167 31.0827 16.9835 31.0827 22.0001C31.0827 27.0167 27.0159 31.0834 21.9993 31.0834C16.9828 31.0834 12.916 27.0167 12.916 22.0001ZM21.9994 23.5834C21.5852 23.5834 21.2494 23.2476 21.2494 22.8334V17.8334C21.2494 17.4192 21.5852 17.0834 21.9994 17.0834C22.4136 17.0834 22.7494 17.4192 22.7494 17.8334V22.8334C22.7494 23.2476 22.4136 23.5834 21.9994 23.5834ZM21.9994 26.1667C22.4596 26.1667 22.8327 25.7936 22.8327 25.3334C22.8327 24.8732 22.4596 24.5001 21.9994 24.5001C21.5391 24.5001 21.166 24.8732 21.166 25.3334C21.166 25.7936 21.5391 26.1667 21.9994 26.1667Z"
                                fill="#B62626"
                            />
                            <path
                                d="M21.9994 23.5834C21.5852 23.5834 21.2494 23.2476 21.2494 22.8334V17.8334C21.2494 17.4192 21.5852 17.0834 21.9994 17.0834C22.4136 17.0834 22.7494 17.4192 22.7494 17.8334V22.8334C22.7494 23.2476 22.4136 23.5834 21.9994 23.5834Z"
                                fill="white"
                            />
                            <path
                                d="M21.9994 26.1667C22.4596 26.1667 22.8327 25.7936 22.8327 25.3334C22.8327 24.8732 22.4596 24.5001 21.9994 24.5001C21.5391 24.5001 21.166 24.8732 21.166 25.3334C21.166 25.7936 21.5391 26.1667 21.9994 26.1667Z"
                                fill="white"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_9205_20709">
                                <rect width="20" height="20" fill="white" transform="translate(12 12)" />
                            </clipPath>
                        </defs>
                    </svg>

                    <ModalCloseButton
                        className={`${baseClass}__close-button`}
                        top={'14px'}
                        color={'#77777C'}
                        // top={{
                        //     base: '14px',
                        //     sm: '30px',
                        // }}
                        // right={{
                        //     base: '14px',
                        //     sm: '40px',
                        // }}
                    />
                </ModalHeader>

                <ModalBody
                    className={`${baseClass}__body`}
                    // px={{
                    //     base: '20px',
                    //     sm: '48px',
                    // }}
                    // pb={'0'}
                >
                    <Text mb={'8px'} fontWeight={600}>
                        {t('10526', 'Exit application form')}
                    </Text>
                    <Text
                        fontSize={{
                            base: '14px',
                            // sm: '16px',
                        }}
                        fontWeight={500}
                    >
                        {t('10527', 'Are you sure you want to exit the application form?')}
                    </Text>
                    <Text
                        fontSize={{
                            base: '14px',
                            // sm: '16px',
                        }}
                        fontWeight={500}
                    >
                        {t('10774', 'Any information you have entered will not be saved')}.
                    </Text>
                </ModalBody>
                <ModalFooter
                    className={`${baseClass}__footer`}
                    // px={{
                    //     base: '20px',
                    //     sm: '48px',
                    // }}
                    // pb={{
                    //     base: '20px',
                    //     sm: '44px',
                    // }}
                    // pt={{
                    //     base: '20px',
                    //     sm: '40px',
                    // }}
                    display={'flex'}
                    gap={'16px'}
                >
                    <button
                        className={`${baseClass}__footer--btn ${baseClass}__footer--btn-cancel`}
                        type={'button'}
                        onClick={() => setIsModalOpen(false)}
                    >
                        {t('10322', 'Keep applying')}
                    </button>
                    {/*<Button width={'100%'} onClick={() => setIsModalOpen(false)}>*/}
                    {/*    {t('10322', 'Keep applying')}*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    width={'100%'}*/}
                    {/*    // variant="primary"*/}
                    {/*    colorScheme={'red'}*/}
                    {/*    onClick={() => {*/}
                    {/*        onCloseApply()*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    {t('10323', 'Exit')}*/}
                    {/*</Button>*/}
                    <button
                        className={`${baseClass}__footer--btn ${baseClass}__footer--btn-confirm`}
                        type={'button'}
                        onClick={handleExit}
                        id={'ra-button-job-post-action-application-form-close-ok'}
                        ra-data-job-post-id={id}
                    >
                        {t('10323', 'Exit')}
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default CloseApplyModal
